import { useState, useEffect, useCallback } from 'react';
import { supabase, CmsAiProduct } from '@/lib/supabase';
import ImageUploader from '@/components/base/ImageUploader';

interface EditForm {
  name: string;
  subtitle: string;
  description: string;
  icon: string;
  tag: string;
  image_url: string;
}

export default function AIProductsCMS() {
  const [list, setList] = useState<CmsAiProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState<number | null>(null);
  const [editData, setEditData] = useState<EditForm | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');

  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast(msg);
    setToastType(type);
    setTimeout(() => setToast(''), 3000);
  };

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('cms_ai_products')
        .select('*')
        .order('sort_order', { ascending: true });
      if (error) throw error;
      setList(data || []);
    } catch {
      showToast('加载失败，请刷新重试', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchList(); }, [fetchList]);

  const handleEdit = (item: CmsAiProduct) => {
    setEditId(item.id);
    setEditData({
      name: item.name || '',
      subtitle: item.subtitle || '',
      description: item.description || '',
      icon: item.icon || '',
      tag: item.tag || '',
      image_url: item.image_url || '',
    });
  };

  const handleSave = async () => {
    if (!editId || !editData) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('cms_ai_products')
        .update({
          name: editData.name,
          subtitle: editData.subtitle,
          description: editData.description,
          icon: editData.icon,
          tag: editData.tag,
          image_url: editData.image_url,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editId);
      if (error) throw error;
      showToast('保存成功');
      setEditId(null);
      setEditData(null);
      await fetchList();
    } catch {
      showToast('保存失败，请重试', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleMoveOrder = async (item: CmsAiProduct, direction: 'up' | 'down') => {
    const idx = list.findIndex((p) => p.id === item.id);
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= list.length) return;
    const swapItem = list[swapIdx];
    try {
      await Promise.all([
        supabase.from('cms_ai_products').update({ sort_order: swapItem.sort_order }).eq('id', item.id),
        supabase.from('cms_ai_products').update({ sort_order: item.sort_order }).eq('id', swapItem.id),
      ]);
      await fetchList();
    } catch {
      showToast('排序失败', 'error');
    }
  };

  const handleTogglePublish = async (item: CmsAiProduct) => {
    try {
      const { error } = await supabase
        .from('cms_ai_products')
        .update({ is_published: !item.is_published, updated_at: new Date().toISOString() })
        .eq('id', item.id);
      if (error) throw error;
      showToast(item.is_published ? '已取消发布' : '已发布');
      await fetchList();
    } catch {
      showToast('操作失败', 'error');
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-gray-800">AI产品管理</h2>
          <p className="text-sm text-gray-400 mt-0.5">
            共 {list.length} 款AI产品 · {list.filter((p) => p.is_published).length} 款已发布
          </p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
              <div className="flex gap-4">
                <div className="w-10 h-10 bg-gray-100 rounded-xl flex-shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-gray-100 rounded w-1/3"></div>
                  <div className="h-3 bg-gray-100 rounded w-2/3"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {list.map((item, idx) => (
            <div
              key={item.id}
              className={`bg-white rounded-xl border p-5 transition-all ${
                item.is_published ? 'border-gray-100' : 'border-gray-200 opacity-70'
              }`}
            >
              {editId === item.id ? (
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">产品代号 (Codename)</label>
                      <input
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 font-mono"
                        value={editData?.name || ''}
                        onChange={(e) => setEditData((d) => d ? { ...d, name: e.target.value } : d)}
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-500 mb-1 block">标签 (Tag)</label>
                      <input
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                        value={editData?.tag || ''}
                        onChange={(e) => setEditData((d) => d ? { ...d, tag: e.target.value } : d)}
                        placeholder="如：NEW / V1.5 / E2E"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">产品副标题</label>
                    <input
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                      value={editData?.subtitle || ''}
                      onChange={(e) => setEditData((d) => d ? { ...d, subtitle: e.target.value } : d)}
                    />
                  </div>
                  <ImageUploader
                    label="产品图片"
                    value={editData?.image_url || ''}
                    onChange={(url) => setEditData((d) => d ? { ...d, image_url: url } : d)}
                    placeholder="粘贴图片 URL，如 https://..."
                    previewWidth="w-40"
                    previewHeight="h-28"
                  />
                  <div>
                    <label className="text-xs text-gray-500 mb-1 block">产品描述</label>
                    <textarea
                      rows={4}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-500 resize-none"
                      value={editData?.description || ''}
                      onChange={(e) => setEditData((d) => d ? { ...d, description: e.target.value } : d)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={handleSave}
                      disabled={saving}
                      className="px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700 cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {saving ? '保存中...' : '保存'}
                    </button>
                    <button
                      onClick={() => { setEditId(null); setEditData(null); }}
                      className="px-4 py-2 bg-gray-100 text-gray-600 text-sm rounded-lg hover:bg-gray-200 cursor-pointer whitespace-nowrap"
                    >
                      取消
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-4">
                  {item.image_url ? (
                    <div className="w-16 h-12 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100">
                      <img src={item.image_url} alt={item.name} className="w-full h-full object-cover object-top" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 flex items-center justify-center bg-gray-900 rounded-xl flex-shrink-0">
                      <i className="ri-brain-line text-teal-400 text-xl"></i>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-xs font-mono text-teal-600">{item.codename || item.name}</span>
                      {item.tag && (
                        <span className="text-xs px-1.5 py-0.5 bg-gray-800 text-white rounded">{item.tag}</span>
                      )}
                      <span className={`text-xs px-2 py-0.5 rounded-full ${item.is_published ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {item.is_published ? '已发布' : '草稿'}
                      </span>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-1">{item.subtitle}</h3>
                    <p className="text-sm text-gray-500 leading-relaxed line-clamp-2">{item.description}</p>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      onClick={() => handleMoveOrder(item, 'up')}
                      disabled={idx === 0}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="上移"
                    >
                      <i className="ri-arrow-up-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleMoveOrder(item, 'down')}
                      disabled={idx === list.length - 1}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg transition-colors cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      title="下移"
                    >
                      <i className="ri-arrow-down-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleEdit(item)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors cursor-pointer"
                      title="编辑"
                    >
                      <i className="ri-edit-line text-base"></i>
                    </button>
                    <button
                      onClick={() => handleTogglePublish(item)}
                      className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors cursor-pointer"
                      title={item.is_published ? '取消发布' : '发布'}
                    >
                      <i className={`${item.is_published ? 'ri-eye-off-line' : 'ri-eye-line'} text-base`}></i>
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {toast && (
        <div className={`fixed bottom-6 right-6 text-white text-sm px-4 py-2.5 rounded-lg shadow-lg z-50 ${toastType === 'error' ? 'bg-rose-600' : 'bg-gray-800'}`}>
          <div className="flex items-center gap-2">
            <i className={`${toastType === 'error' ? 'ri-error-warning-line text-rose-200' : 'ri-checkbox-circle-line text-emerald-400'}`}></i>
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
